import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { useTranslation } from "react-i18next";
import { TablePage } from "@outpave/ds";
import ContactsTable from "../../components/admin/contacts/ContactsTable";
import ContactType from "../../enums/ContactType";
import ContactVerificationStatus from "../../enums/ContactVerificationStatus";
import { ContactRouterDrawerType } from "../../enums/ContactRouterDrawerType";
import ContactDrawerRouter from "components/admin/contacts/ContactDrawerRouter";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { UserAdd01Icon } from "@hugeicons-pro/core-stroke-rounded";
// import ImportContactsModal from "components/admin/contacts/ImportContactsModal";

export type ContactFilterCriteriaProps = {
  contactType?: string;
  searchTerm?: string;
  status: string[];
  verificationStatus?: string[];
};

const Contacts: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [refreshContacts, setRefreshContacts] = useState(0);
  // const [openImportContacts, setOpenImportContacts] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<ContactRouterDrawerType | null>(
    null,
  );
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null,
  );

  const [filterCriteria, setFilterCriteria] =
    useState<ContactFilterCriteriaProps>({
      contactType: ContactType.Individual,
      searchTerm: "",
      status: ["ACTIVE"],
    });

  const tabConfigurations = [
    {
      label: t("Individuals"),
      contactType: ContactType.Individual,
      status: ["ACTIVE"],
    },
    {
      label: t("Businesses"),
      contactType: ContactType.Business,
      status: ["ACTIVE"],
    },
    {
      label: t("Unverified"),
      contactType: ContactType.Individual,
      status: ["ACTIVE"],
      verificationStatus: [ContactVerificationStatus.Unverified],
    },
  ];

  useEffect(() => {
    setFilterCriteria({
      contactType: tabConfigurations[activeTab].contactType,
      searchTerm: searchTerm,
      status: tabConfigurations[activeTab].status,
      verificationStatus: tabConfigurations[activeTab].verificationStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const handleRefreshContacts = () => {
    setRefreshContacts((prev) => prev + 1);
  };

  // Unified open handler for all flows
  const openContactDrawer = (
    type: ContactRouterDrawerType,
    contactId: string | null = null,
  ) => {
    setDrawerType(type);
    setSelectedContactId(contactId);
    setDrawerOpen(true);
  };

  // Table actions
  const handleOpenEditContactDrawer = (contactId?: string) => {
    openContactDrawer(
      ContactRouterDrawerType.EditingContact,
      contactId || null,
    );
  };

  const handleOpenContactDetails = (contactId: string) => {
    navigate(`/admin/contacts/${contactId}`);
  };

  const handleOpenCreateContactDrawer = () => {
    openContactDrawer(ContactRouterDrawerType.NewContact, null);
  };

  // shared/src/dModels/services/Notification.js builds /contacts?id=<id> as a
  // notification email button, so links already in inboxes land here. Redirect
  // rather than drop it, and replace the entry so Back does not bounce.
  const resetParam = useGetParamFromUrl("id", (contactId: string) =>
    navigate(`/admin/contacts/${contactId}`, { replace: true }),
  );

  // Drawer close handler
  const handleCloseDrawer = (
    refresh: boolean = false,
    openContactDetailsWithId?: string,
  ) => {
    if (refresh) handleRefreshContacts();
    resetParam();

    setSelectedContactId(null);
    setDrawerOpen(false);
    setDrawerType(null);

    // A contact just created lands on its own page, which is where viewing
    // one lives now.
    if (openContactDetailsWithId) {
      navigate(`/admin/contacts/${openContactDetailsWithId}`);
    }
  };

  return (
    <TablePage
      title={t("Contacts")}
      primaryAction={
        <Button
          variant="primary-filled"
          startIcon={<HugeiconsIcon icon={UserAdd01Icon} size={16} />}
          onClick={handleOpenCreateContactDrawer}
        >
          {t("Add Contact")}
        </Button>
      }
      // TODO: restore when CSV import is implemented. Nothing reads the
      // chosen file yet, so the button opened a dialog that silently
      // discarded it. See the TODO on components/common/ImportCsvModal.
      // secondaryAction={
      //   <Button
      //     variant="default-outlined"
      //     startIcon={<HugeiconsIcon icon={Upload01Icon} size={16} />}
      //     onClick={() => setOpenImportContacts(true)}
      //   >
      //     {t("Import")}
      //   </Button>
      // }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={search}
    >
      <ContactsTable
        filterCriteria={filterCriteria}
        refreshContacts={refreshContacts}
        handleOpenViewDetailsDrawer={handleOpenContactDetails}
        handleOpenEditDetailsDrawer={handleOpenEditContactDrawer}
        handleRefreshContacts={handleRefreshContacts}
      />

      {/* <ImportContactsModal
        open={openImportContacts}
        // The tab decides which record is being imported, so it decides the
        // template as well.
        contactType={tabConfigurations[activeTab].contactType}
        onClose={() => setOpenImportContacts(false)}
      /> */}

      {drawerType && (
        <ContactDrawerRouter
          open={drawerOpen}
          drawerType={drawerType}
          contactId={selectedContactId || undefined}
          onClose={handleCloseDrawer}
          refreshContacts={handleRefreshContacts}
        />
      )}
    </TablePage>
  );
};

export default Contacts;
